import { AxiosError, AxiosResponse } from "axios";
import { Flex, LoadingIcon } from "basis";
import { Account } from "library/accounts/types";
import { isLocalMocks } from "library/api/api";
import { getHref, postHref } from "library/api/httpMethods";
import { Product } from "library/hooks/types";
import { useAccountInfo } from "library/hooks/useAccountInfo";
import { useLayout } from "library/hooks/useLayout";
import { checkFields } from "library/utils";
import { FC, useEffect, useReducer } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "../Layout/Layout";
import { AboutYourself } from "./AboutYourself";
import { Approved } from "./Approved";
import { Assessed } from "./Assessed";
import { ConfirmMmp } from "./ConfirmMmp";
import { Error } from "./Error";
import { FinalCancel } from "./FinalCancel";
import { FinalDecline } from "./FinalDecline";
import { ImportantInformation } from "./ImportantInformation";
import { IncomeAndExpenses } from "./IncomeAndExpenses";
import { NewCreditLimit } from "./NewCreditLimit";
import { NzForm } from "./NzForm";
import { NzSuccess } from "./NzSuccess";
import { Otp } from "./Otp";
import { ProofOfIncome } from "./ProofOfIncome";
import { reducer } from "./reducer/increaseCreditLimitReducer";
import { initialState as init } from "./reducer/initialState";
import { ReviewDetails } from "./ReviewDetails";
import {
  Action,
  ActionType,
  ApplicationStatus,
  EmploymentStatusType,
  GetApplicationResponse,
  PageInfo,
  PageType,
  PersonalDetailsErrorResponse,
  PersonalDetailsResponse,
  ScreenComponentType,
  ScreenState,
  SessionResponse,
  State,
} from "./types";
import { UnableToIncrease } from "./UnableToIncrease";
import {
  GetApplicationResponseData,
  parseGetApplicationResponse,
  parsePersonalDetailsErrorResponse,
  parseSessionResponse,
} from "./utils";
import { VerifierTerms } from "./VerifierTerms";

/* cspell: disable-next-line */
const INELIGIBLE_PRODUCT_CODES = ["CCNZCLN"];

const HEADING = "Request a credit limit increase";
const pageInfo: PageInfo = {
  [PageType.LANDING]: {
    heading: HEADING,
    screenComponent: () => null,
  },
  [PageType.OTP]: {
    heading: HEADING,
    screenComponent: Otp,
  },
  [PageType.NEW_CREDIT_LIMIT]: {
    heading: HEADING,
    screenComponent: NewCreditLimit,
  },
  [PageType.IMPORTANT_INFORMATION]: {
    heading: HEADING,
    screenComponent: ImportantInformation,
  },
  [PageType.ABOUT_YOURSELF]: {
    heading: HEADING,
    screenComponent: AboutYourself,
  },
  [PageType.INCOME_AND_EXPENSES]: {
    heading: HEADING,
    screenComponent: IncomeAndExpenses,
  },
  [PageType.REVIEW_DETAILS]: {
    heading: HEADING,
    screenComponent: ReviewDetails,
  },
  [PageType.VERIFIER_TERMS]: {
    heading: HEADING,
    screenComponent: VerifierTerms,
  },
  [PageType.FINAL_DECLINE]: {
    heading: HEADING,
    screenComponent: FinalDecline,
  },
  [PageType.CONFIRM_MMP]: {
    heading: HEADING,
    screenComponent: ConfirmMmp,
  },
  [PageType.ASSESSED]: {
    heading: HEADING,
    screenComponent: Assessed,
  },
  [PageType.UNABLE_TO_INCREASE]: {
    heading: HEADING,
    screenComponent: UnableToIncrease,
  },
  [PageType.PROOF_OF_INCOME]: {
    heading: HEADING,
    screenComponent: ProofOfIncome,
  },
  [PageType.APPROVED]: {
    heading: HEADING,
    screenComponent: Approved,
  },
  [PageType.NZ_FORM]: {
    heading: HEADING,
    screenComponent: NzForm,
  },
  [PageType.NZ_SUCCESS]: {
    heading: HEADING,
    screenComponent: NzSuccess,
  },
  [PageType.ERROR]: {
    heading: HEADING,
    screenComponent: Error,
  },
  [PageType.FINAL_CANCEL]: {
    heading: HEADING,
    screenComponent: FinalCancel,
  },
};

interface ScreenProps {
  screenComponent: ScreenComponentType;
  state: ScreenState;
  dispatch: React.Dispatch<Action>;
}

const IncreaseCreditLimitScreen: FC<ScreenProps> = ({
  screenComponent: ScreenComponent,
  state,
  dispatch,
}) => {
  const { scrollToTop } = useLayout();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, ScreenComponent]);

  return <ScreenComponent state={state} dispatch={dispatch} />;
};

type SessionVariables = {
  createSessionUrl: string;
  tokenId: string;
};

type Props = {
  location: {
    state?: {
      account: Account;
      product: Product;
    };
  };
  initialState?: State;
};

export const IncreaseCreditLimit = ({
  location,
  initialState = init, // 🚨 Adding ability to inject initial state which will be used for testing purposes.
}: Props) => {
  const { account, product } = location.state || {};
  const accountInfo = useAccountInfo(account);

  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { page } = state;
  const isLandingPage = page === PageType.LANDING;
  const { heading: pageHeading, screenComponent } = pageInfo[page];
  const getApplicationUrl = account?.links.find(
    (link) => link.rel === "credit_limit_increase",
  )?.href;
  const personalDetailsUrl = account?.links.find(
    (link) => link.rel === "get_personal_details_unmasked",
  )?.href;

  const sessionInfo = useMutation(
    async ({ createSessionUrl, tokenId }: SessionVariables) => {
      const response = await postHref<SessionResponse>(createSessionUrl, {
        tokenId,
      });
      const responseData = parseSessionResponse(response.data);

      if (responseData === null) {
        throw new window.Error("Invalid session response received");
      }

      return responseData;
    },
  );
  const createSession = (createSessionUrl: string) => {
    if (accountInfo === null) {
      return;
    }

    sessionInfo.mutate(
      {
        createSessionUrl,
        tokenId: accountInfo.cardToken,
      },
      {
        onSuccess: (responseData) => {
          dispatch({
            type: ActionType.OTP_REQUIRED,
            accountInfo,
            maskedMobileNumber: responseData.maskedMobileNumber,
            otpVerifyUrl: responseData.otpVerifyUrl,
            otpResendUrl: responseData.otpResendUrl,
          });
        },
        onError: () => {
          dispatch({
            type: ActionType.OTP_SESSION_CREATION_FAILED,
            accountInfo,
          });
        },
      },
    );
  };

  const applicationInfo = useQuery<GetApplicationResponseData, AxiosError>(
    ["cli-application", { url: getApplicationUrl }],
    async () => {
      const response = await getHref<GetApplicationResponse>(getApplicationUrl);
      const responseData = parseGetApplicationResponse(response.data);
      if (
        responseData === null ||
        responseData.application.status ===
          ApplicationStatus.INTERNAL_SERVICE_ERROR
      ) {
        throw new window.Error("Invalid application response received");
      }

      return responseData;
    },
    {
      cacheTime: 0,
      enabled:
        isLandingPage &&
        getApplicationUrl !== undefined &&
        accountInfo !== null &&
        accountInfo.isNZ === false,
      onSuccess(responseData: GetApplicationResponseData) {
        if (accountInfo === null) {
          return;
        }
        const {
          application,
          bankConnectUrl,
          latitudePoiUrl,
          cancelUrl,
          acknowledgementUrl,
        } = responseData;

        const { status } = application;

        switch (status) {
          case ApplicationStatus.CLI_NOT_ELIGIBLE: {
            dispatch({
              type: ActionType.CLI_NOT_ELIGIBLE,
              accountInfo,
            });
            return;
          }

          case ApplicationStatus.CLI_PENDING_ASSESSMENT:
          case ApplicationStatus.CLI_IN_PROGRESS:
          case ApplicationStatus.CLI_PENDING_CUST_CALL: {
            dispatch({
              type: ActionType.CLI_PENDING_CUST_CALL,
              accountInfo,
            });
            return;
          }

          case ApplicationStatus.CLI_PENDING_POI: {
            dispatch({
              type: ActionType.CLI_PENDING_POI,
              accountInfo,
              bankConnectUrl: bankConnectUrl as string,
              expiryDate: application.expiry_date,
              latitudePoiUrl: latitudePoiUrl as string,
              employmentStatus:
                application.employment_status as EmploymentStatusType,
              cancelUrl: cancelUrl as string,
            });
            return;
          }

          case ApplicationStatus.CLI_CA_PENDING_ACCEPTANCE:
          case ApplicationStatus.CLI_PENDING_ACCEPTANCE:
          case ApplicationStatus.CLI_STA_PENDING_ACCEPTANCE: {
            dispatch({
              type: ActionType.CLI_STA_PENDING_ACCEPTANCE,
              accountInfo,
              requestedCreditLimit: application.requested_credit_limit,
              approvedCreditLimit: application.approved_credit_limit,
              isConditionallyApproved: status === "CLI_CA_PENDING_ACCEPTANCE",
              acknowledgementUrl: acknowledgementUrl as string,
              cancelUrl: cancelUrl as string,
            });
            return;
          }
          case ApplicationStatus.CLI_FINAL_CANCEL: {
            dispatch({
              type: ActionType.CLI_FINAL_CANCEL,
              accountInfo,
            });
          }
        }
      },
      onError: (error) => {
        if (accountInfo === null) {
          return;
        }

        if (
          error.response?.data.title === ApplicationStatus.CLI_ACCOUNT_STATUS
        ) {
          dispatch({
            type: ActionType.CLI_NOT_ELIGIBLE,
            accountInfo,
          });
          return;
        }

        dispatch({
          type: ActionType.GET_APPLICATION_FAILED,
          accountInfo,
        });
      },
    },
  );

  const shouldGetPersonalDetails =
    isLandingPage &&
    personalDetailsUrl !== undefined &&
    accountInfo !== null &&
    accountInfo.isNZ === false &&
    [
      undefined,
      ApplicationStatus.CLI_NOT_ELIGIBLE,
      ApplicationStatus.CLI_PENDING_ASSESSMENT,
    ].includes(applicationInfo.data?.application.status) === false;

  const personalDetailsInfo = useQuery<
    AxiosResponse<PersonalDetailsResponse>,
    AxiosError<PersonalDetailsErrorResponse>
  >(
    ["personal-details", { url: personalDetailsUrl }],
    () =>
      getHref<PersonalDetailsResponse>(
        personalDetailsUrl,
        {},
        isLocalMocks && state.sessionExpiry
          ? {
              "Fake-Session-Expiry": String(state.sessionExpiry), // header values are always strings
            }
          : {},
      ),
    {
      enabled: shouldGetPersonalDetails,
      onError: (error) => {
        if (accountInfo === null) {
          return;
        }
        if (error.response?.status === 401) {
          const errorResponseData = parsePersonalDetailsErrorResponse(
            error.response.data,
          );

          if (errorResponseData !== null) {
            createSession(errorResponseData.createSessionUrl);
            return;
          }
        }

        dispatch({
          type: ActionType.GET_PERSONAL_DETAILS_FAILED,
          accountInfo,
        });
      },
    },
  );

  useEffect(() => {
    if (!account) {
      navigate("/");
    }
  }, [account, navigate]);

  useEffect(() => {
    if (accountInfo === null) {
      return;
    }

    if (INELIGIBLE_PRODUCT_CODES.includes(accountInfo.productCode)) {
      dispatch({
        type: ActionType.CLI_NOT_ELIGIBLE,
        accountInfo,
      });
      return;
    }

    // NZ
    if (accountInfo.isNZ) {
      dispatch({
        type: ActionType.ACCOUNT_INFO_LOADED_NZ,
        accountInfo,
      });
      return;
    }

    // AU
    if (personalDetailsInfo.data && applicationInfo.data) {
      const {
        residential: userAddress,
        mobile_phone: userMobilePhoneNumber,
        home_phone: userHomePhoneNumber,
        work_phone: userWorkPhoneNumber,
      } = personalDetailsInfo.data.data;

      dispatch({
        type: ActionType.ALL_DATA_LOADED,
        accountInfo,
        userAddress,
        userMobilePhoneNumber,
        userHomePhoneNumber,
        userWorkPhoneNumber,
        submitUrl: applicationInfo.data.submitUrl,
        acknowledgementUrl: applicationInfo.data.acknowledgementUrl,
      });
    }
  }, [accountInfo, personalDetailsInfo, applicationInfo, product]);

  return (
    <Layout>
      <Layout.Header>{pageHeading}</Layout.Header>
      <Layout.Main>
        {checkFields(state, "accountInfo") ? (
          <IncreaseCreditLimitScreen
            screenComponent={screenComponent}
            state={state}
            dispatch={dispatch}
          />
        ) : (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        )}
      </Layout.Main>
    </Layout>
  );
};
